import { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import CanonicalTag from '../components/CanonicalTag';
import ecoLivingImage from '../assets/eco-living-adu.jpg';

const HowToUseADUEcoLiving = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);
  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-for-eco-living-or-off-grid-retreat" />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-6">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
                How to Use Your ADU for Eco-Living or Off-Grid Retreat
              </h1>
              <div className="flex justify-center mb-8">
                <button 
                  onClick={openCalendlyModal}
                  className="bg-brand-orange text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                >
                  📅 Book & Schedule
                </button>
              </div>
            </div>

            {/* Main Image */}
            <div className="mb-16">
              <img 
                src={ecoLivingImage} 
                alt="Eco-Living ADU" 
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>

            {/* Content Sections */}
            <div className="space-y-16">
              {/* Section 1 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Sustainable Living in Your Own Backyard
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Design an ADU that minimizes impact while maximizing comfort—ideal for eco-conscious homeowners or minimalist lifestyle seekers.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Solar Ready</h3>
                    <p className="text-muted-foreground">Solar roof panels and energy-efficient design for minimal environmental impact</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Water Conservation</h3>
                    <p className="text-muted-foreground">Rainwater capture and greywater systems for sustainable water use</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Natural Materials</h3>
                    <p className="text-muted-foreground">Reclaimed wood, organic finishes, and toxin-free building materials</p>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => window.location.href = '/landing'}
                    className="bg-white text-brand-orange border-2 border-brand-orange px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange hover:text-white transition-colors"
                  >
                    📐 What Fits My Lot?
                  </button>
                  <button 
                    onClick={() => window.location.href = '/adu-calculator'}
                    className="bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                  >
                    💰 Get Your Price & ROI Report
                  </button>
                </div>
              </section>

              {/* Section 2 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Core Features for Green Living
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-2xl font-semibold mb-4" style={{ color: '#E4683B' }}>Sustainable Systems</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>• Solar roof or solar-ready design</li>
                      <li>• Rainwater capture & greywater systems</li>
                      <li>• Composting or low-flow toilet systems</li>
                      <li>• Natural building materials and finishes</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-4" style={{ color: '#E4683B' }}>Wellness Benefits</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li>• Clean, toxin-free interiors</li>
                      <li>• Natural ventilation and airflow</li>
                      <li>• Organic textures and materials</li>
                      <li>• Connection to nature and outdoors</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 3 */}
              <section>
                <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
                  Wellness and Environmental ROI
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Experience the benefits of sustainable living while reducing your environmental footprint and utility costs.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Low Utility Bills</h3>
                    <p className="text-muted-foreground">Energy-efficient design and renewable systems dramatically reduce operating costs</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Health Benefits</h3>
                    <p className="text-muted-foreground">Clean, toxin-free interiors promote better health and well-being</p>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold mb-4" style={{ color: '#E4683B' }}>Tax Incentives</h3>
                    <p className="text-muted-foreground">Qualify for tax incentives and rebates for energy-efficient housing features</p>
                  </div>
                </div>
              </section>
            </div>

            {/* Bottom CTAs */}
            <div className="text-center mt-16">
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button 
                  onClick={() => window.open('/our-work', '_blank')}
                  className="bg-white text-brand-orange border-2 border-brand-orange px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange hover:text-white transition-colors"
                >
                  🏡 Our Portfolio
                </button>
                <button 
                  onClick={() => window.open('https://uda-adu.com/faq', '_blank')}
                  className="bg-brand-orange text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-orange/90 transition-colors"
                >
                  ❓ Our FAQ
                </button>
              </div>
            </div>
          </div>
        </main>

        <Footer />

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[90vh] m-4">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-semibold">Schedule a Consultation</h3>
                <button 
                  onClick={closeCalendlyModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <iframe 
                src="https://calendly.com/udaadu"
                width="100%" 
                height="100%" 
                frameBorder="0"
                title="Schedule a meeting"
                className="rounded-b-lg"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default HowToUseADUEcoLiving;